import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Truck } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.3 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HEADLINE = ["YOU WERE", "MADE FOR", "MORE."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const goShop = () => document.querySelector("#products")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 bg-cover bg-center"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/media/hero-bg.png')" }}
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-[#050505]/60" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-20 md:pb-28"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-7 inline-flex items-center gap-3 self-start bg-white/5 backdrop-blur-md border border-white/15 px-4 py-2"
          data-testid="hero-delivery-badge"
        >
          <Truck size={15} className="text-[#E6DFD4]" />
          <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#E6DFD4]">
            Free UAE Delivery on orders above AED 1,000
          </span>
        </motion.div>

        <h1 data-testid="hero-headline" className="font-display uppercase leading-[0.82] tracking-tight text-[#F5F5F5]">
          {HEADLINE.map((l, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block text-[18vw] md:text-[12vw] lg:text-[10rem]"
              >
                {l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-7 max-w-xl text-base md:text-lg text-[#C9C4BB] leading-relaxed"
        >
          Real peptides. Real results. For athletes and anyone serious about transforming their body and life.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-9 flex items-center gap-6"
        >
          <button
            data-testid="hero-shop-cta"
            onClick={goShop}
            className="group inline-flex items-center gap-3 bg-[#E6DFD4] text-black px-9 py-4 text-xs tracking-[0.22em] uppercase font-bold hover:bg-white transition-colors duration-300"
          >
            Shop Peptides
            <ArrowDown size={16} className="group-hover:translate-y-1 transition-transform duration-300" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 right-5 md:right-10 z-10 hidden sm:flex items-center gap-3 text-[#737373]"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <div className="h-10 w-px bg-[#737373]/50" />
      </motion.div>
    </section>
  );
};

export default Hero;
